import React, { useEffect, useState } from 'react';
import { checkIfHasFullAccess, fetchMachineMotionConfigInfo, setDefaultHost } from './Main/main';
import { getPendantAvailability } from '@src/Main/pendantHelpers.js';
import { AppRouter } from './app_router';
import { Spinner } from '@src/components';
import { MachineLogicFactory } from '@vse/main';
import { machineLogicServices } from './MachineLogicServices';
import { ConfigInfoProvider, RemoteAccessProvider, WifiNetworksProvider } from '@src/contexts';
import { RemoteSupportProvider } from '@ventionco/remote-support-components';
import { EnvironmentVariables } from '@src/EnvironmentVariables';

export const App = () => {
  const [isPendantAvailable, setIsPendantAvailable] = useState(/** @type {boolean} */ null);
  const [hasFullAccess, setHasFullAccess] = useState(/** @type {boolean} */ null);
  const [library, setLibrary] = useState(/** @type {Library} */ null);
  const [configInfo, setConfigInfo] = useState(/** @type {ConfigInfo} */ null);
  const [hasSetDefaultHost, setHasSetDefaultHost] = useState(false);

  useEffect(() => {
    const ISLOCAL = false; // HIS MUST ALWAYS BE FALSE BEFORE BEING MERGED TO WEBSITE
    setDefaultHost(ISLOCAL).then(() => {
      setHasSetDefaultHost(true);
    });
  }, []);

  useEffect(() => {
    if (!hasSetDefaultHost) return;
    checkIfHasFullAccess(setHasFullAccess);
  }, [hasSetDefaultHost]);

  // NOTE: for simplicity, even if we have a configInfo context,
  // we will fetch the config here to build the router once (needed for the AppLauncher routes)
  // we do that to not wrap the App in ConfigInfoProvider
  useEffect(() => {
    if (!hasSetDefaultHost) return;
    fetchMachineMotionConfigInfo(setConfigInfo);
  }, [hasSetDefaultHost]);

  useEffect(() => {
    if (!hasSetDefaultHost) return;
    if (EnvironmentVariables.CI_RUN) {
      setIsPendantAvailable(false);
      return;
    }
    getPendantAvailability(setIsPendantAvailable, 5000);
  }, [hasSetDefaultHost]);

  useEffect(() => {
    if (!configInfo) {
      return;
    }

    MachineLogicFactory.createLibrary(configInfo, machineLogicServices, { pythonDocumentationUrl: EnvironmentVariables.PYTHON_DOCUMENTATION_URL }).then(setLibrary);
  }, [configInfo]);

  // It is important to not render the AppRouter before all the data is in.
  // See the note in AppRouter for more details.
  if (hasFullAccess === null ||
    configInfo === null ||
    library === null ||
    isPendantAvailable === null
  ) {
    return <Spinner spinnerText={'Loading ControlCenter'}/>;
  }

  return (
    <RemoteSupportProvider
      railsUrl={EnvironmentVariables.RAILS_URL}
      machineCloudClientUrl={EnvironmentVariables.MACHINE_CLOUD_CLIENT_URL}
      machineCloudApiUrl={EnvironmentVariables.MACHINE_CLOUD_API_URL}
      isTestingRemoteSupport={EnvironmentVariables.IS_TESTING_REMOTE_SUPPORT}
    >
    <RemoteAccessProvider>
      <ConfigInfoProvider>
        <WifiNetworksProvider isPendantAvailable={isPendantAvailable}>
          <AppRouter
            hasFullAccess={hasFullAccess}
            configInfo={configInfo}
            library={library}
            isPendantAvailable={isPendantAvailable}
          />
        </WifiNetworksProvider>
      </ConfigInfoProvider>
    </RemoteAccessProvider>
    </RemoteSupportProvider>
  );
};
